import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Button,
  Avatar,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Edit as EditIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  ArrowUpward as PromoteIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { colors } from '../theme/expertiseTheme';
import { getStoredUser, isAdmin as checkIsAdmin, Role, Sector, registerUser } from '../services/auth';
import {
  getUsers,
  changeUserRole,
  changeUserSector,
  promoteToAdmin,
  promoteToStaff,
  deleteUser,
  AdminUser,
} from '../services/admin';
import { getSectors, SectorOption } from '../services/tools';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Cores por role
const roleColors: Record<string, string> = {
  Admin: colors.primary,
  Staff: '#7C3AED',
  User: '#6B7280',
};

// Cores por setor
const sectorColors: Record<string, string> = {
  Financeiro: colors.primary,
  Comercial: '#3B82F6',
  TI: '#7C3AED',
  RH: '#EC4899',
  Operacoes: '#F59E0B',
};

const ConfiguracoesPage: React.FC = () => {
  const user = getStoredUser();
  const isAdmin = checkIsAdmin(user);

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile state
  const [profileName, setProfileName] = useState(user?.email?.split('@')[0] || '');

  // Admin state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [newRole, setNewRole] = useState<Role>('User');
  const [newSector, setNewSector] = useState<Sector>('Financeiro');

  // Register state
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regSector, setRegSector] = useState<Sector | ''>('');
  const [regRole, setRegRole] = useState<Role>('User');
  const [sectorOptions, setSectorOptions] = useState<SectorOption[]>([]);
  const [loadingSectors, setLoadingSectors] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const roles: Role[] = ['Admin', 'Staff', 'User'];
  const sectors: Sector[] = ['Financeiro', 'Comercial', 'TI', 'RH', 'Operacoes'];

  // Carregar usuários e setores se for admin
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadSectors();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar usuários');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadSectors = async () => {
    setLoadingSectors(true);
    try {
      const response = await getSectors();
      setSectorOptions(response.sectors);
    } catch {
      setSectorOptions([
        { value: 'Financeiro', label: 'Financeiro' },
        { value: 'Comercial', label: 'Comercial' },
        { value: 'TI', label: 'TI' },
        { value: 'RH', label: 'RH' },
        { value: 'Operacoes', label: 'Operações' },
      ]);
    } finally {
      setLoadingSectors(false);
    }
  };

  const handleRegisterUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!regEmail.trim()) {
      setError('Informe o email do usuário');
      return;
    }

    if (!regSector) {
      setError('Selecione um setor');
      return;
    }

    if (regPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (regPassword !== regConfirmPassword) {
      setError('As senhas não conferem');
      return;
    }

    setRegisterLoading(true);
    try {
      await registerUser({
        email: regEmail,
        password: regPassword,
        sector: regSector,
      });

      setRegisteredEmail(regEmail);
      setRegisterSuccess(true);

      // Limpar formulário
      setRegEmail('');
      setRegPassword('');
      setRegConfirmPassword('');
      setRegSector('');
      setRegRole('User');

      // Atualizar lista de usuários
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Erro ao registrar usuário');
    } finally {
      setRegisterLoading(false);
    }
  };

  const resetRegisterForm = () => {
    setRegisterSuccess(false);
    setRegisteredEmail('');
    setRegEmail('');
    setRegPassword('');
    setRegConfirmPassword('');
    setRegSector('');
    setRegRole('User');
    setError('');
    setSuccess('');
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
  };

  const handleSaveProfile = () => {
    setSuccess('Perfil atualizado com sucesso!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const openEditDialog = (userToEdit: AdminUser) => {
    setSelectedUser(userToEdit);
    setNewRole(userToEdit.roles[0] || 'User');
    setNewSector(userToEdit.sector);
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError('');

    try {
      // Atualizar role se mudou
      if (newRole !== selectedUser.roles[0]) {
        await changeUserRole(selectedUser.id, newRole);
      }

      // Atualizar setor se mudou
      if (newSector !== selectedUser.sector) {
        await changeUserSector(selectedUser.id, newSector);
      }

      setSuccess('Usuário atualizado com sucesso!');
      setEditDialogOpen(false);
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteToAdmin = async (userId: string) => {
    if (!window.confirm('Tem certeza que deseja promover este usuário para Administrador?')) {
      return;
    }

    setLoading(true);
    try {
      await promoteToAdmin(userId);
      setSuccess('Usuário promovido para Admin!');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Erro ao promover usuário');
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteToStaff = async (userId: string) => {
    if (!window.confirm('Tem certeza que deseja promover este usuário para Staff?')) {
      return;
    }

    setLoading(true);
    try {
      await promoteToStaff(userId);
      setSuccess('Usuário promovido para Staff!');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Erro ao promover usuário');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (userToRemove: AdminUser) => {
    setUserToDelete(userToRemove);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setLoading(true);
    setError('');

    try {
      await deleteUser(userToDelete.id);
      setSuccess(`Usuário ${userToDelete.email} removido com sucesso!`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Erro ao remover usuário');
    } finally {
      setLoading(false);
    }
  };

  const renderProfileTab = () => (
    <Grid container spacing={4}>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                mx: 'auto',
                mb: 2,
                fontSize: '2.5rem',
                fontWeight: 600,
                backgroundColor: roleColors[user?.roles?.[0] || 'User'],
              }}
            >
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>

            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark, mb: 1 }}>
              {profileName || user?.email}
            </Typography>

            <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 2 }}>
              {user?.email}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 2 }}>
              <Chip
                label={user?.roles?.[0] || 'User'}
                size="small"
                sx={{
                  backgroundColor: `${roleColors[user?.roles?.[0] || 'User']}15`,
                  color: roleColors[user?.roles?.[0] || 'User'],
                  fontWeight: 500,
                }}
              />
              {user?.sector && (
                <Chip
                  label={user.sector}
                  size="small"
                  sx={{
                    backgroundColor: `${sectorColors[user.sector]}15`,
                    color: sectorColors[user.sector],
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>

            <Button
              variant="outlined"
              size="small"
              sx={{
                borderColor: colors.border,
                color: colors.text.primary,
              }}
            >
              Alterar foto
            </Button>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark, mb: 3 }}>
              Informações do Perfil
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome de exibição"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ color: colors.text.light, mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={user?.email || ''}
                  disabled
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ color: colors.text.light, mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Setor"
                  value={user?.sector || 'Não definido'}
                  disabled
                  InputProps={{
                    startAdornment: <BusinessIcon sx={{ color: colors.text.light, mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Permissão"
                  value={user?.roles?.[0] || 'User'}
                  disabled
                  InputProps={{
                    startAdornment: <SecurityIcon sx={{ color: colors.text.light, mr: 1 }} />,
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark, mb: 3 }}>
              Segurança
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: colors.border,
                  color: colors.text.primary,
                }}
              >
                Alterar senha
              </Button>
              <Button
                variant="outlined"
                sx={{
                  borderColor: colors.border,
                  color: colors.text.primary,
                }}
              >
                Configurar 2FA
              </Button>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSaveProfile}
                sx={{
                  backgroundColor: colors.accent,
                  color: colors.dark,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#c9e835',
                  },
                }}
              >
                Salvar alterações
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderAdminTab = () => (
    <Card>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark }}>
              Gerenciamento de Usuários
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {users.length} usuário{users.length !== 1 ? 's' : ''} cadastrado{users.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadUsers}
            disabled={loadingUsers}
            sx={{
              borderColor: colors.border,
              color: colors.text.primary,
            }}
          >
            Atualizar
          </Button>
        </Box>

        {loadingUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Usuário</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Setor</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: roleColors[u.roles[0] || 'User'],
                            fontSize: '0.875rem',
                          }}
                        >
                          {u.email.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {u.email}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.text.light }}>
                            ID: {u.id.substring(0, 8)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={u.roles[0] || 'User'}
                        size="small"
                        sx={{
                          backgroundColor: `${roleColors[u.roles[0] || 'User']}15`,
                          color: roleColors[u.roles[0] || 'User'],
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={u.sector}
                        size="small"
                        sx={{
                          backgroundColor: `${sectorColors[u.sector]}15`,
                          color: sectorColors[u.sector],
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip
                          label={u.emailConfirmed ? 'Email ✓' : 'Email ✗'}
                          size="small"
                          sx={{
                            backgroundColor: u.emailConfirmed
                              ? 'rgba(0, 99, 0, 0.1)'
                              : 'rgba(220, 38, 38, 0.1)',
                            color: u.emailConfirmed ? colors.primary : colors.error,
                            fontSize: '0.7rem',
                          }}
                        />
                        {u.twoFactorEnabled && (
                          <Chip
                            label="2FA"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(59, 130, 246, 0.1)',
                              color: '#3B82F6',
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEditDialog(u)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {u.roles[0] !== 'Admin' && (
                          <>
                            <Tooltip title="Promover para Staff">
                              <IconButton
                                size="small"
                                onClick={() => handlePromoteToStaff(u.id)}
                                disabled={u.roles[0] === 'Staff'}
                              >
                                <PromoteIcon
                                  fontSize="small"
                                  sx={{ color: u.roles[0] === 'Staff' ? colors.text.light : '#7C3AED' }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Promover para Admin">
                              <IconButton
                                size="small"
                                onClick={() => handlePromoteToAdmin(u.id)}
                              >
                                <AdminIcon fontSize="small" sx={{ color: colors.primary }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {u.id !== user?.id && (
                          <Tooltip title="Remover usuário">
                            <IconButton
                              size="small"
                              onClick={() => openDeleteDialog(u)}
                            >
                              <DeleteIcon fontSize="small" sx={{ color: colors.error }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  const renderRegisterTab = () => {
    if (registerSuccess) {
      return (
        <Card>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 99, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 40, color: colors.primary }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 700, color: colors.dark, mb: 2 }}>
              Usuário registrado!
            </Typography>

            <Typography sx={{ color: colors.text.secondary, mb: 2, lineHeight: 1.6 }}>
              O convite de confirmação foi enviado para:
            </Typography>

            <Box
              sx={{
                backgroundColor: colors.light,
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                py: 2,
                px: 3,
                mb: 4,
                display: 'inline-block',
              }}
            >
              <Typography sx={{ fontWeight: 600, color: colors.dark, fontSize: '1.125rem' }}>
                {registeredEmail}
              </Typography>
            </Box>

            <Alert
              severity="info"
              sx={{
                mb: 4,
                textAlign: 'left',
                borderRadius: '12px',
                backgroundColor: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
              }}
            >
              <Typography variant="body2" sx={{ color: colors.text.primary }}>
                O usuário precisa confirmar o email clicando no link enviado antes de poder fazer login.
              </Typography>
            </Alert>

            <Button
              variant="contained"
              onClick={resetRegisterForm}
              startIcon={<PersonAddIcon />}
              sx={{
                backgroundColor: colors.accent,
                color: colors.dark,
                fontWeight: 600,
                py: 1.25,
                px: 4,
                '&:hover': {
                  backgroundColor: '#c9e835',
                },
              }}
            >
              Registrar outro usuário
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark, mb: 0.5 }}>
              Registrar Novo Usuário
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              Crie uma conta para um novo membro da equipe
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleRegisterUser} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{ fontSize: '0.875rem', fontWeight: 500, color: colors.text.primary, mb: 1 }}
                >
                  Email
                </Typography>
                <TextField
                  fullWidth
                  placeholder="usuario@empresa.com"
                  type="email"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: colors.text.light, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: colors.white } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{ fontSize: '0.875rem', fontWeight: 500, color: colors.text.primary, mb: 1 }}
                >
                  Setor
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={regSector}
                    onChange={(e) => setRegSector(e.target.value as Sector)}
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ color: colors.text.light, fontSize: 20 }} />
                      </InputAdornment>
                    }
                    disabled={loadingSectors}
                    sx={{
                      backgroundColor: colors.white,
                      '& .MuiSelect-select': { py: 1.75 },
                    }}
                  >
                    <MenuItem value="" disabled>
                      {loadingSectors ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          Carregando...
                        </Box>
                      ) : (
                        'Selecione o setor'
                      )}
                    </MenuItem>
                    {sectorOptions.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{ fontSize: '0.875rem', fontWeight: 500, color: colors.text.primary, mb: 1 }}
                >
                  Senha
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Min. 6 caracteres"
                  type={showRegPassword ? 'text' : 'password'}
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: colors.text.light, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowRegPassword(!showRegPassword)}
                          edge="end"
                          sx={{ color: colors.text.light }}
                        >
                          {showRegPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: colors.white } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{ fontSize: '0.875rem', fontWeight: 500, color: colors.text.primary, mb: 1 }}
                >
                  Confirmar Senha
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Repita a senha"
                  type={showRegConfirmPassword ? 'text' : 'password'}
                  value={regConfirmPassword}
                  onChange={(e) => setRegConfirmPassword(e.target.value)}
                  required
                  error={regConfirmPassword.length > 0 && regPassword !== regConfirmPassword}
                  helperText={
                    regConfirmPassword.length > 0 && regPassword !== regConfirmPassword
                      ? 'As senhas não conferem'
                      : ''
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: colors.text.light, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowRegConfirmPassword(!showRegConfirmPassword)}
                          edge="end"
                          sx={{ color: colors.text.light }}
                        >
                          {showRegConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: colors.white } }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={registerLoading || loadingSectors}
                startIcon={
                  registerLoading ? <CircularProgress size={18} /> : <PersonAddIcon />
                }
                sx={{
                  py: 1.25,
                  px: 4,
                  backgroundColor: colors.accent,
                  color: colors.dark,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#c9e835',
                  },
                  '&:disabled': {
                    backgroundColor: colors.border,
                    color: colors.text.light,
                  },
                }}
              >
                {registerLoading ? 'Registrando...' : 'Registrar Usuário'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout title="Configurações" subtitle="Gerencie suas preferências e configurações do sistema">
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: `1px solid ${colors.border}`,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 56,
            },
            '& .Mui-selected': {
              color: colors.primary,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: colors.primary,
            },
          }}
        >
          <Tab
            icon={<PersonIcon />}
            iconPosition="start"
            label="Perfil"
          />
          {isAdmin && (
            <Tab
              icon={<AdminIcon />}
              iconPosition="start"
              label="Administração"
            />
          )}
          {isAdmin && (
            <Tab
              icon={<PersonAddIcon />}
              iconPosition="start"
              label="Registro de Usuários"
            />
          )}
        </Tabs>
      </Card>

      <TabPanel value={tabValue} index={0}>
        {renderProfileTab()}
      </TabPanel>

      {isAdmin && (
        <TabPanel value={tabValue} index={1}>
          {renderAdminTab()}
        </TabPanel>
      )}

      {isAdmin && (
        <TabPanel value={tabValue} index={2}>
          {renderRegisterTab()}
        </TabPanel>
      )}

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Usuário</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 3 }}>
                {selectedUser.email}
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newRole}
                  label="Role"
                  onChange={(e) => setNewRole(e.target.value as Role)}
                >
                  {roles.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Setor</InputLabel>
                <Select
                  value={newSector}
                  label="Setor"
                  onChange={(e) => setNewSector(e.target.value as Sector)}
                >
                  {sectors.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: colors.text.secondary }}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: colors.accent,
              color: colors.dark,
              '&:hover': {
                backgroundColor: '#c9e835',
              },
            }}
          >
            {loading ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Remover Usuário</DialogTitle>
        <DialogContent>
          {userToDelete && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
                Esta ação é irreversível. O usuário perderá todo o acesso ao sistema.
              </Alert>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  backgroundColor: colors.light,
                  borderRadius: '12px',
                  p: 2,
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: roleColors[userToDelete.roles[0] || 'User'],
                    fontSize: '1rem',
                  }}
                >
                  {userToDelete.email.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>
                    {userToDelete.email}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    {userToDelete.roles[0] || 'User'} &bull; {userToDelete.sector}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setUserToDelete(null);
            }}
            sx={{ color: colors.text.secondary }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: colors.error,
              color: '#fff',
              '&:hover': {
                backgroundColor: '#b91c1c',
              },
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default ConfiguracoesPage;

